import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cake, Loader2 } from 'lucide-react';
import { setBirthday, type BirthdayView } from '@/lib/api';
import formStyles from '@/components/Form.module.css';
import styles from './PhoneCaptureModal.module.css';

export interface BirthdayCaptureModalProps {
  /** Called AFTER the birthday is persisted, with the fresh view. */
  onSaved: (view: BirthdayView) => void;
  onClose: () => void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
/** Max day per month (Feb allows 29 so a leap-day birthday is storable). */
const MAX_DAY = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Set-once birthday capture (month + day; no year). Mirrors PhoneCaptureModal's
 * chrome (reuses its CSS module). The server enforces set-once — a second save
 * 409s — so this is the only place a birthday is entered.
 */
export function BirthdayCaptureModal({ onSaved, onClose }: BirthdayCaptureModalProps) {
  const { t } = useTranslation('settings');
  const [month, setMonth] = useState(1);
  const [day, setDay] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const maxDay = MAX_DAY[month - 1] as number;
  const safeDay = Math.min(day, maxDay);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const view = await setBirthday(month, safeDay);
      onSaved(view);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('birthdayModal.error'));
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-birthday-title" onClick={onClose}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.iconWrap} aria-hidden>
          <Cake size={64} />
        </div>
        <h2 id="evi-birthday-title" className={styles.title}>{t('birthdayModal.title')}</h2>
        <p className={styles.reason}>{t('birthdayModal.reason')}</p>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {errorMsg && <div className={styles.error} role="alert">{errorMsg}</div>}
          <div className={styles.fieldRow}>
            <label className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>{t('birthdayModal.month')}</span>
              <select
                className={formStyles.input}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                disabled={submitting}
              >
                {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
              </select>
            </label>
            <label className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>{t('birthdayModal.day')}</span>
              <select
                className={formStyles.input}
                value={safeDay}
                onChange={(e) => setDay(Number(e.target.value))}
                disabled={submitting}
              >
                {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.actions}>
            <button type="submit" className={styles.saveBtn} disabled={submitting}>
              {submitting
                ? <><Loader2 size={14} className={styles.spin} /> {t('birthdayModal.saving')}</>
                : t('birthdayModal.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
